import React, { useState, useEffect, useContext } from 'react';
import PaymentTab from '../PaymentTab';
import ChargesTab from '../ChargesTab';
import RentalAgreementModal from '../RentalAgreementModal';
import { fetchTenancyLedger } from '../../services/tenancyService';
import { formatDate } from '../../utils/dateUtils';
import { AuthContext } from '../../contexts/AuthContext';

const AgreementDetailsSection = ({
  selectedAgreement,
  selectedListing,
  agencyCurrency,
  property,
  activeTab,
  onTabChange,
  onAgreementUpdate // Add callback for when agreement is updated
}) => {
  const { user, agencyCountry } = useContext(AuthContext);
  const [tenancyLedger, setTenancyLedger] = useState(null);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  // Add modal state for RentalAgreementModal
  const [showRentalAgreementModal, setShowRentalAgreementModal] = useState(false);

  // Fetch tenancy ledger when tenancy_ledger_id changes
  useEffect(() => {
    const tenancyLedgerId = selectedListing?.current_tenancy?.tenancy_ledger_id;

    if (!tenancyLedgerId) {
      setTenancyLedger(null);
      setLedgerError(null);
      return;
    }

    const fetchLedgerData = async () => {
      setIsLoadingLedger(true);
      setLedgerError(null);

      try {
        const ledgerData = await fetchTenancyLedger(tenancyLedgerId);
        setTenancyLedger(ledgerData);
      } catch (error) {
        console.error('Error fetching tenancy ledger:', error);
        setLedgerError('Failed to load tenancy ledger data');
        setTenancyLedger(null);
      } finally {
        setIsLoadingLedger(false);
      }
    };

    fetchLedgerData();
  }, [selectedListing?.current_tenancy?.tenancy_ledger_id]);

  // Handler for saving agreement changes
  const handleSaveAgreement = async (updatedAgreement) => {
    // Call parent callback to refresh agreement data
    if (onAgreementUpdate) {
      await onAgreementUpdate(updatedAgreement);
    }
    setShowRentalAgreementModal(false);
  };

  // Helper to refresh tenancy ledger data after updates
  const refreshTenancyLedger = async () => {
    const tenancyLedgerId = selectedListing?.current_tenancy?.tenancy_ledger_id;
    if (!tenancyLedgerId) return;

    try {
      const ledgerData = await fetchTenancyLedger(tenancyLedgerId);
      setTenancyLedger(ledgerData);
    } catch (error) {
      console.error('Error refreshing tenancy ledger:', error);
    }
  };


  // Get status from listing.current_tenancy.status (string)
  const tenancyStatus = selectedListing?.current_tenancy?.status || 'Unknown';

  if (!selectedAgreement) {
    return (
      <>
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white border-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-file-text me-2"></i>
                Current Agreement
              </h5>
              <button
                className="btn btn-primary"
                onClick={() => setShowRentalAgreementModal(true)}
              >
                <i className="bi bi-file-text me-2"></i>
                Manage Agreements
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="alert alert-info text-center">
              <i className="bi bi-info-circle me-2"></i>
              No active rental agreement
            </div>
          </div>
        </div>

        {/* Rental Agreement Modal - For creating/editing agreement details */}
        {showRentalAgreementModal && (
          <RentalAgreementModal
            show={showRentalAgreementModal}
            onClose={() => setShowRentalAgreementModal(false)}
            selectedListing={selectedListing}
            onSave={handleSaveAgreement}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* Agreement Details Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-file-text me-2"></i>
              Current Agreement
            </h5>
            <button
              className="btn btn-primary"
              onClick={() => setShowRentalAgreementModal(true)}
            >
              <i className="bi bi-file-text me-2"></i>
              Manage Agreements
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <small className="text-muted">Tenant(s)</small>
              <div className="fw-semibold">
                {selectedAgreement.renters && selectedAgreement.renters.length > 0
                  ? selectedAgreement.renters.map(r => `${r.person?.given || r.given || r.first_name || ''} ${r.person?.surname || r.surname || r.last_name || ''}`.trim()).join(', ')
                  : 'No tenant assigned'
                }
              </div>
            </div>
            <div className="col-md-3">
              <small className="text-muted">Start Date</small>
              <div className="fw-semibold">
                {selectedAgreement.start_date ? formatDateWithContext(selectedAgreement.start_date) : 'N/A'}
              </div>
            </div>
            <div className="col-md-3">
              <small className="text-muted">End Date</small>
              <div className="fw-semibold">
                {selectedAgreement.end_date ? formatDateWithContext(selectedAgreement.end_date) : 'N/A'}
              </div>
            </div>
            <div className="col-md-3">
              <small className="text-muted">Status</small>
              <div className="fw-semibold">
                <span className={`badge ${
                  tenancyStatus === 'active' ? 'bg-success' : 
                  tenancyStatus === 'pending' ? 'bg-warning' : 
                  'bg-secondary'
                }`}>
                  {tenancyStatus.charAt(0).toUpperCase() + tenancyStatus.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {selectedAgreement.amount && (
            <div className="row g-3 mt-2">
              <div className="col-md-3">
                <small className="text-muted">Rent Amount</small>
                <div className="fw-semibold">
                  {selectedAgreement.amount?.formatted || 'N/A'}
                </div>
              </div>
              <div className="col-md-3">
                <small className="text-muted">Frequency</small>
                <div className="fw-semibold">{selectedAgreement.rent_frequency || selectedAgreement.frequency || 'N/A'}</div>
              </div>
              <div className="col-md-3">
                <small className="text-muted">Next Rent Due</small>
                <div className="fw-semibold">
                  {selectedListing?.current_tenancy?.next_rent_due_date
                    ? formatDateWithContext(selectedListing.current_tenancy.next_rent_due_date)
                    : 'N/A'}
                </div>
              </div>
              <div className="col-md-3">
                <small className="text-muted">Payment Type</small>
                <div className="fw-semibold">
                  {selectedAgreement.payment_type === 'prepay' ? 'prepay' :
                   selectedAgreement.payment_type === 'postpay' ? 'postpay' :
                   selectedAgreement.payment_type || '—'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Financial Summary Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="bg-light p-3 rounded text-center">
                <small className="text-muted d-block mb-2">Total Payments</small>
                <h4 className="text-success mb-0">
                  {tenancyLedger?.total_payments?.formatted || 'N/A'}
                </h4>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bg-light p-3 rounded text-center">
                <small className="text-muted d-block mb-2">Total Charges</small>
                <h4 className="text-danger mb-0">
                  {tenancyLedger?.total_charges?.formatted || 'N/A'}
                </h4>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bg-light p-3 rounded text-center">
                <small className="text-muted d-block mb-2">Balance</small>
                <h4 className={`mb-0 ${tenancyLedger?.balance?.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                  {tenancyLedger?.balance?.formatted || tenancyLedger?.Balance?.formatted || 'N/A'}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <ul className="nav nav-pills nav-fill mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => onTabChange('payments')}
          >
            <i className="bi bi-cash-coin me-2"></i>
            Payments
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'charges' ? 'active' : ''}`}
            onClick={() => onTabChange('charges')}
          >
            <i className="bi bi-receipt me-2"></i>
            Charges
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div>
        {/* Loading State */}
        {isLoadingLedger && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading ledger data...</span>
            </div>
            <p className="text-muted mt-2">Loading tenancy ledger...</p>
          </div>
        )}

        {/* Error State */}
        {ledgerError && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {ledgerError}
            <button
              className="btn btn-outline-danger btn-sm ms-2"
              onClick={() => {
                const tenancyLedgerId = selectedListing?.current_tenancy?.tenancy_ledger_id;
                if (tenancyLedgerId) {
                  setLedgerError(null);
                  const fetchLedgerData = async () => {
                    setIsLoadingLedger(true);
                    try {
                      const ledgerData = await fetchTenancyLedger(tenancyLedgerId);
                      setTenancyLedger(ledgerData);
                    } catch (error) {
                      setLedgerError('Failed to load tenancy ledger data');
                    } finally {
                      setIsLoadingLedger(false);
                    }
                  };
                  fetchLedgerData();
                }
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Tab Content - Only show when data is loaded or when there's no tenancy ledger ID */}
        {!isLoadingLedger && !ledgerError && (
          <>
            {activeTab === 'payments' && (
              <PaymentTab
                tenancyLedger={tenancyLedger}
                property={property}
                selectedListing={selectedListing}
                onDataUpdate={refreshTenancyLedger}
              />
            )}
            {activeTab === 'charges' && (
              <ChargesTab
                tenancyLedger={tenancyLedger}
                selectedListing={selectedListing}
                property={property}
                onDataUpdate={refreshTenancyLedger}
              />
            )}
          </>
        )}
      </div>

      {/* Rental Agreement Modal - For editing agreement details */}
      {showRentalAgreementModal && (
        <RentalAgreementModal
          show={showRentalAgreementModal}
          onClose={() => setShowRentalAgreementModal(false)}
          selectedListing={selectedListing}
          onSave={handleSaveAgreement}
        />
      )}
    </>
  );
};

export default AgreementDetailsSection;
